import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, forwardRef, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SafeHtml } from '@angular/platform-browser';
import { ClrLoadingModule, ClrProgressBarModule } from '@clr/angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, forkJoin, from } from 'rxjs';
import { concatMap, reduce } from 'rxjs/operators';

import { CoreImportsModule } from '@svg-frontends/core-imports';

import { DialogModule } from '../../dialog.module';
import { DialogLevel } from '../../enums/dialog-level.enum';
import { DialogSize } from '../../enums/dialog-size.enum';

export interface ActionProgressDialogData<T> {
	requests: Observable<T>[]; // Array of Observables which will be chunked
	body: SafeHtml;
	submitButton: string;
	title: string;

	cancelButton?: string;
	level?: DialogLevel;
	size?: DialogSize;
}

export enum ActionProgressDialogState {
	CANCEL,
	SUCCESS,
	ERROR,
}

export interface ActionProgressDialogResult<T> {
	state: ActionProgressDialogState;

	error?: Error;
	value?: T[];
}

interface ProgressStep {
	error?: Error;
	iconName: string;
	isFinal: boolean;
	labelText: string;
	value: number;
}

/**
 * This Dialog shows the header/body etc. messages same as the ActionDialogComponent
 * and has a progress-bar to display the progress of the chunked Observable handling (e.g. BE-Calls)
 */
@UntilDestroy()
@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'svg-frontends-action-progress-dialog',
	templateUrl: './action-progress-dialog.component.html',
	imports: [DialogModule, ClrProgressBarModule, NgClass, ClrLoadingModule, AsyncPipe, forwardRef(() => CoreImportsModule)],
})
export class ActionProgressDialogComponent<T> implements OnInit {
	inputArgs = inject<ActionProgressDialogData<T>>(MAT_DIALOG_DATA);
	private readonly matDialogRef = inject<MatDialogRef<ActionProgressDialogComponent<T>, ActionProgressDialogResult<T>>>(MatDialogRef);

	private readonly MAX_CHUNK_SIZE: number = 4; // Max parallel endpoint-requests

	steps$: BehaviorSubject<ProgressStep> = new BehaviorSubject<ProgressStep>(null);
	requestCount: number;

	DIALOG_LEVEL = DialogLevel;
	DIALOG_SIZE = DialogSize;

	loading = false;

	ngOnInit(): void {
		this.requestCount = this.inputArgs.requests.length / this.MAX_CHUNK_SIZE;
		this.steps$.next({
			iconName: 'success-standard',
			value: 0,
			isFinal: false,
			labelText: 'Daten erfolgreich geladen.',
		});
	}

	submitDialog(): void {
		if (this.inputArgs.requests && this.inputArgs.requests.length > 0) {
			this.loading = true;
			const chunkedRequestArray: Observable<T>[][] = [];
			for (let i = 0; i < this.inputArgs.requests.length; i += this.MAX_CHUNK_SIZE) {
				chunkedRequestArray.push(this.inputArgs.requests.slice(i, i + this.MAX_CHUNK_SIZE));
			}
			let value = 0;
			const stepLength = this.requestCount / chunkedRequestArray.length;

			from(chunkedRequestArray)
				.pipe(
					concatMap((requestChunk: Observable<T>[]): Observable<T[]> => {
						value += stepLength;
						this.steps$.next({
							iconName: 'success-standard',
							isFinal: false,
							labelText: 'Daten erfolgreich geladen.',
							value: value,
						});
						return forkJoin(requestChunk);
					}),
					reduce((acc: T[], response: T[]): T[] => [...acc, ...response], []),
					untilDestroyed(this),
				)
				.subscribe(
					(value) => {
						this.steps$.next({
							iconName: 'success-standard',
							isFinal: true,
							labelText: 'Daten erfolgreich geladen.',
							value: this.requestCount,
						});
						this.matDialogRef.close({ state: ActionProgressDialogState.SUCCESS, value });
					},
					(error: Error) => {
						this.steps$.next({
							iconName: 'warning-standard',
							isFinal: true,
							error: error,
							labelText: 'Es ist ein Fehler aufgetreten',
							value: this.steps$.value.value,
						});
						this.matDialogRef.close({ state: ActionProgressDialogState.ERROR, error });
					},
				);
		}
	}

	closeDialog(): void {
		this.matDialogRef.close({ state: ActionProgressDialogState.CANCEL });
	}
}
